<template>
  <Teleport to="body">
    <div
      v-if="showBanner"
      class="legal-grace-banner"
      role="dialog"
      aria-labelledby="legal-grace-title"
      aria-live="polite"
    >
      <strong id="legal-grace-title" class="legal-grace-title">Termos e privacidade atualizados</strong>
      <p class="legal-grace-text">
        Aceite a versão atual dos documentos para manter sua conta em conformidade.
        <template v-if="graceEndsLabel">
          Após <span class="legal-grace-em">{{ graceEndsLabel }}</span>, o acesso ao sistema ficará disponível apenas após o aceite.
        </template>
      </p>
      <div class="legal-grace-actions">
        <router-link :to="{ name: 'aceite-legal' }" class="legal-grace-btn legal-grace-btn-primary">
          Revisar e aceitar
        </router-link>
        <button type="button" class="legal-grace-btn legal-grace-btn-muted" @click="dismiss">
          Dispensar por agora
        </button>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';

const SESSION_DISMISS_KEY = 'legal_grace_reminder_dismissed';

const store = useStore();
const route = useRoute();
const dismissed = ref(false);

const user = computed(() => store.state.auth.user);
const legal = computed(() => user.value?.legal);

const skipRouteNames = new Set([
  'aceite-legal',
  'login',
  'register',
  'recuperar-senha',
  'redefinir-senha',
  'google-callback',
  'sso-login',
  'confirm-email-boxed',
  'termos-de-servico',
  'politica-de-privacidade',
]);

const showBanner = computed(() => {
  if (!store.getters['auth/isAuthenticated']) {
    return false;
  }
  const l = legal.value;
  if (!l?.consents_active || !l.pending_acceptance) {
    return false;
  }
  if (l.blocking_enforced) {
    return false;
  }
  if (skipRouteNames.has(route.name)) {
    return false;
  }
  if (dismissed.value) {
    return false;
  }
  if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(SESSION_DISMISS_KEY) === '1') {
    return false;
  }
  return true;
});

const graceEndsLabel = computed(() => {
  const iso = legal.value?.grace_period_ends_at;
  if (!iso) {
    return '';
  }
  try {
    return new Date(iso).toLocaleDateString('pt-BR', { dateStyle: 'long' });
  } catch {
    return '';
  }
});

function dismiss() {
  dismissed.value = true;
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(SESSION_DISMISS_KEY, '1');
  }
}

watch(
  () => legal.value?.pending_acceptance,
  (pending) => {
    if (!pending) {
      dismissed.value = false;
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem(SESSION_DISMISS_KEY);
      }
    }
  }
);

watch(
  () => legal.value?.blocking_enforced,
  (blocking) => {
    if (blocking && typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(SESSION_DISMISS_KEY);
    }
  }
);
</script>

<style scoped>
.legal-grace-banner {
  position: fixed;
  bottom: 1rem;
  right: 1rem;
  left: 1rem;
  max-width: 44rem;
  margin-left: auto;
  z-index: 1080;
  padding: 1rem 1.1rem;
  background: #fff;
  color: #1a1a1a;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  box-shadow:
    0 16px 48px rgba(0, 0, 0, 0.14),
    0 6px 16px rgba(0, 0, 0, 0.1),
    0 1px 3px rgba(0, 0, 0, 0.06);
}

@media (min-width: 576px) {
  .legal-grace-banner {
    left: auto;
  }
}

.legal-grace-title {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #111;
  line-height: 1.35;
}

.legal-grace-text {
  margin: 0.5rem 0 0;
  font-size: 0.8125rem;
  line-height: 1.45;
  color: #444;
}

.legal-grace-em {
  color: #111;
  font-weight: 500;
}

.legal-grace-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.85rem;
}

.legal-grace-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.4rem 0.75rem;
  font-size: 0.8125rem;
  font-weight: 500;
  text-decoration: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.legal-grace-btn-primary {
  background: #111;
  color: #fff;
  border: 1px solid #111;
}

.legal-grace-btn-primary:hover {
  background: #333;
  border-color: #333;
  color: #fff;
}

.legal-grace-btn-muted {
  background: transparent;
  color: #555;
  border: 1px solid rgba(0, 0, 0, 0.15);
}

.legal-grace-btn-muted:hover {
  color: #111;
  border-color: rgba(0, 0, 0, 0.28);
}
</style>
